import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import RichTextEditor from "@/components/admin/RichTextEditor";
import { LEGAL_DEFAULTS } from "@/lib/legalDefaults";

const PAGES = [
  { key: "privacy", label: "Privacy Policy", path: "/privacy" },
  { key: "terms", label: "Terms of Use", path: "/terms" },
  { key: "cookies", label: "Cookie Policy", path: "/cookies" },
];

export default function LegalManager() {
  const [active, setActive] = useState("privacy");
  const [docs, setDocs] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/legal").then(({ data }) => setDocs(data || {})).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const def = LEGAL_DEFAULTS[active];
  const cur = docs[active] || {};
  const bodyEn = cur.body_en ?? "";
  const bodyAr = cur.body_ar ?? "";

  const set = (field, val) => setDocs((prev) => ({ ...prev, [active]: { ...prev[active], [field]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/legal/${active}`, { body_en: bodyEn || def.body_en, body_ar: bodyAr || def.body_ar });
      toast.success(`${PAGES.find((p) => p.key === active).label} saved and published.`);
    } catch { toast.error("Save failed."); } finally { setSaving(false); }
  };

  const loadDefault = (lang) => {
    if (!window.confirm("Replace the current text with the built-in template? Unsaved changes will be lost.")) return;
    set(lang === "ar" ? "body_ar" : "body_en", lang === "ar" ? def.body_ar : def.body_en);
  };

  if (loading) return <p className="text-muted-text">Loading legal pages…</p>;

  return (
    <div data-testid="admin-legal">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-4">
        <h1 className="text-3xl font-light text-ivory">Legal Pages</h1>
        <button onClick={save} disabled={saving} className="btn-solid" data-testid="legal-save"><Save size={15} strokeWidth={1.5} /> {saving ? "Saving…" : "Save & Publish"}</button>
      </div>
      <p className="text-sm text-muted-text mb-2">Edit your Privacy Policy, Terms of Use and Cookie Policy (English & Arabic). Content is Saudi PDPL-aligned by default.</p>
      <p className="text-xs text-luxury-gold/80 mb-6">⚠️ This is a starting template — please have it reviewed by legal counsel before relying on it.</p>

      <div className="flex gap-2 mb-8 flex-wrap">
        {PAGES.map((p) => (
          <button key={p.key} onClick={() => setActive(p.key)} data-testid={`legal-tab-${p.key}`}
            className={`px-4 py-2 text-xs uppercase tracking-wide border transition-colors ${active === p.key ? "border-primary-gold text-primary-gold" : "border-white/10 text-muted-text hover:text-soft-stone"}`}>{p.label}</button>
        ))}
        <a href={PAGES.find((p) => p.key === active).path} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 text-xs text-muted-text hover:text-primary-gold" data-testid="legal-view"><ExternalLink size={13} /> View live page</a>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="eyebrow">English</label>
            <button onClick={() => loadDefault("en")} className="text-[11px] text-muted-text hover:text-primary-gold" data-testid="legal-default-en">Load template</button>
          </div>
          <RichTextEditor value={bodyEn} onChange={(v) => set("body_en", v)} dir="ltr" testid="legal-editor-en" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="eyebrow">العربية</label>
            <button onClick={() => loadDefault("ar")} className="text-[11px] text-muted-text hover:text-primary-gold" data-testid="legal-default-ar">تحميل القالب</button>
          </div>
          <RichTextEditor value={bodyAr} onChange={(v) => set("body_ar", v)} dir="rtl" testid="legal-editor-ar" />
        </div>
      </div>
    </div>
  );
}
